import { useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';
import {
  AtSign,
  Bell,
  Briefcase,
  Cake,
  Eye,
  Home,
  ImageOff,
  KeyRound,
  LayoutDashboard,
  Loader2,
  Mail,
  MapPin,
  Phone,
  ShieldAlert,
  Slack,
  Trash2,
  Upload,
  User,
} from 'lucide-react';
import { PageHeader } from '@/components/layout/PageHeader';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { PhoneNumberInput } from '@/components/ui/PhoneNumberInput';
import { Label } from '@/components/ui/Label';
import { Textarea } from '@/components/ui/Textarea';
import { Skeleton } from '@/components/ui/Skeleton';
import { Switch } from '@/components/ui/Switch';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/Avatar';
import { ROLE_LABELS } from '@/lib/permissions';
import { Badge } from '@/components/ui/Badge';
import { useAuth } from '@/app/AuthProvider';
import {
  dashboardPrivacyApplies,
  readDashboardDefaultVisible,
  writeDashboardDefaultVisible,
} from '@/lib/dashboardVisibility';
import {
  useMyProfile,
  useRemoveAvatar,
  useUpdateMyProfile,
  useUploadAvatar,
  type ProfileRow,
} from '@/features/settings/hooks';
import { useCredentialStatus } from '@/features/workMode/hooks';
import { seededAvatar } from '@/features/digitalOffice/avatars';
import { SetPinDialog } from '@/components/production/PeopleTab';
import type { WorkPerson } from '@/features/workMode/useLookups';

/**
 * The fields the Settings page lets the user edit. Mirrors the
 * nullable text/date columns added in migration 46. Role / is_active
 * / email / org_id are deliberately NOT in this draft — they're
 * either immutable or admin-controlled (Roles & users page).
 */
type EditableProfile = Pick<
  ProfileRow,
  | 'full_name'
  | 'pronouns'
  | 'phone'
  | 'personal_email'
  | 'date_of_birth'
  | 'bio'
  | 'timezone'
  | 'job_title'
  | 'start_date'
  | 'address_line1'
  | 'address_line2'
  | 'city'
  | 'state'
  | 'postal_code'
  | 'country'
  | 'emergency_contact_name'
  | 'emergency_contact_phone'
  | 'emergency_contact_relationship'
  | 'slack_member_id'
>;

const EDITABLE_FIELDS: (keyof EditableProfile)[] = [
  'full_name',
  'pronouns',
  'phone',
  'personal_email',
  'date_of_birth',
  'bio',
  'timezone',
  'job_title',
  'start_date',
  'address_line1',
  'address_line2',
  'city',
  'state',
  'postal_code',
  'country',
  'emergency_contact_name',
  'emergency_contact_phone',
  'emergency_contact_relationship',
  'slack_member_id',
];

const MAX_AVATAR_BYTES = 4 * 1024 * 1024; // 4 MB

// Mirrors resolveSlackUser() in notify-deliver, which otherwise falls back
// to an email lookup. W… ids come from Enterprise Grid workspaces.
const SLACK_MEMBER_ID_RE = /^[UW][A-Z0-9]{6,}$/;

/** Cheap, reasonable timezone list — IANA ids from the browser when
 * available (Chrome ≥ 95 / FF ≥ 95), otherwise a curated US-centric
 * fallback so the picker still works in older runtimes. */
function getTimezones(): string[] {
  type IntlWithTz = typeof Intl & {
    supportedValuesOf?: (key: string) => string[];
  };
  const i18n = Intl as IntlWithTz;
  if (typeof i18n.supportedValuesOf === 'function') {
    try {
      return i18n.supportedValuesOf('timeZone');
    } catch {
      /* fall through */
    }
  }
  return [
    'America/New_York',
    'America/Chicago',
    'America/Denver',
    'America/Los_Angeles',
    'America/Phoenix',
    'America/Anchorage',
    'Pacific/Honolulu',
    'UTC',
  ];
}

function emptyToNull<T extends Record<string, unknown>>(obj: T): T {
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(obj)) {
    out[k] = typeof v === 'string' && v.trim() === '' ? null : v;
  }
  return out as T;
}

export function SettingsPage() {
  const { profile: authProfile, role, user } = useAuth();
  const { data: profile, isLoading } = useMyProfile();

  const update = useUpdateMyProfile();
  const uploadAvatar = useUploadAvatar();
  const removeAvatar = useRemoveAvatar();

  const fileInputRef = useRef<HTMLInputElement | null>(null);

  // Work Mode PIN (self-service). The dialog is keyed off a WorkPerson
  // so the same component serves managers on the Workstations page.
  const { data: credentials } = useCredentialStatus();
  const myCredential = user ? credentials?.get(user.id) : undefined;
  const [pinOpen, setPinOpen] = useState(false);
  const pinPerson: WorkPerson | null =
    pinOpen && user && profile
      ? {
          id: user.id,
          name: profile.full_name || profile.email,
          email: profile.email,
          role: profile.role,
          jobTitle: profile.job_title ?? null,
          isActive: true,
          avatarUrl: profile.avatar_url ?? null,
          config: seededAvatar(user.id),
        }
      : null;

  const showDashboardPrivacy = dashboardPrivacyApplies(role);
  const [dashboardDefaultVisible, setDashboardDefaultVisible] = useState(() =>
    readDashboardDefaultVisible(role),
  );

  // Local draft state. We hydrate it from the loaded row and keep it
  // in sync if the row changes underneath us (e.g. an avatar upload
  // pushes a fresh refetch). The "dirty" comparison is computed
  // against `profile` so a save that lands the new values back into
  // the cache flips the form back to clean automatically.
  const [draft, setDraft] = useState<EditableProfile | null>(null);

  useEffect(() => {
    if (!profile) return;
    setDraft({
      full_name: profile.full_name,
      pronouns: profile.pronouns,
      phone: profile.phone,
      personal_email: profile.personal_email,
      date_of_birth: profile.date_of_birth,
      bio: profile.bio,
      timezone: profile.timezone,
      job_title: profile.job_title,
      start_date: profile.start_date,
      address_line1: profile.address_line1,
      address_line2: profile.address_line2,
      city: profile.city,
      state: profile.state,
      postal_code: profile.postal_code,
      country: profile.country,
      emergency_contact_name: profile.emergency_contact_name,
      emergency_contact_phone: profile.emergency_contact_phone,
      emergency_contact_relationship: profile.emergency_contact_relationship,
      slack_member_id: profile.slack_member_id,
    });
  }, [profile]);

  const isDirty = useMemo(() => {
    if (!profile || !draft) return false;
    return EDITABLE_FIELDS.some((k) => {
      const a = (draft[k] ?? '') as string;
      const b = (profile[k] ?? '') as string;
      return a !== b;
    });
  }, [draft, profile]);

  const timezones = useMemo(() => getTimezones(), []);

  const slackMemberIdDraft = draft?.slack_member_id?.trim().toUpperCase() ?? '';
  const slackMemberIdInvalid =
    slackMemberIdDraft !== '' && !SLACK_MEMBER_ID_RE.test(slackMemberIdDraft);

  const setField = <K extends keyof EditableProfile>(key: K, value: EditableProfile[K]) => {
    setDraft((prev) => (prev ? { ...prev, [key]: value } : prev));
  };

  const onSave = async () => {
    if (!draft) return;
    if (slackMemberIdInvalid) {
      toast.error('Slack member ID should look like U012ABCDEF.');
      return;
    }
    try {
      // Trim trailing whitespace + collapse blank-string-to-null so we
      // don't store '' alongside null and cause confusing "Cleared" /
      // "Set to empty string" diffs in the audit log.
      const patch = emptyToNull(
        Object.fromEntries(
          EDITABLE_FIELDS.map((k) => [
            k,
            typeof draft[k] === 'string' ? (draft[k] as string).trim() : draft[k],
          ]),
        ) as EditableProfile,
      );
      if (patch.slack_member_id) patch.slack_member_id = patch.slack_member_id.toUpperCase();
      await update.mutateAsync(patch);
      toast.success('Settings saved.');
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Could not save settings.';
      toast.error(msg);
    }
  };

  const onReset = () => {
    if (!profile) return;
    setDraft({
      full_name: profile.full_name,
      pronouns: profile.pronouns,
      phone: profile.phone,
      personal_email: profile.personal_email,
      date_of_birth: profile.date_of_birth,
      bio: profile.bio,
      timezone: profile.timezone,
      job_title: profile.job_title,
      start_date: profile.start_date,
      address_line1: profile.address_line1,
      address_line2: profile.address_line2,
      city: profile.city,
      state: profile.state,
      postal_code: profile.postal_code,
      country: profile.country,
      emergency_contact_name: profile.emergency_contact_name,
      emergency_contact_phone: profile.emergency_contact_phone,
      emergency_contact_relationship: profile.emergency_contact_relationship,
      slack_member_id: profile.slack_member_id,
    });
  };

  const onPickAvatar = () => fileInputRef.current?.click();

  const onAvatarChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = ''; // allow re-selecting the same file
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error('Please choose an image file.');
      return;
    }
    if (file.size > MAX_AVATAR_BYTES) {
      toast.error('Image is too large (max 4 MB).');
      return;
    }

    try {
      await uploadAvatar.mutateAsync({
        file,
        previousPath: profile?.avatar_path ?? null,
      });
      toast.success('Profile photo updated.');
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Upload failed.';
      toast.error(msg);
    }
  };

  const onRemoveAvatar = async () => {
    try {
      await removeAvatar.mutateAsync({ path: profile?.avatar_path ?? null });
      toast.success('Profile photo removed.');
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Could not remove photo.';
      toast.error(msg);
    }
  };

  const initials = (profile?.full_name || profile?.email || 'U')
    .split(/[.\s@]/)
    .filter(Boolean)
    .slice(0, 2)
    .map((s) => s[0]?.toUpperCase())
    .join('') || 'U';

  return (
    <div className="space-y-6">
      <PageHeader
        title="Settings"
        actions={
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              onClick={onReset}
              disabled={!isDirty || update.isPending}
            >
              Discard
            </Button>
            <Button onClick={onSave} disabled={!isDirty || update.isPending}>
              {update.isPending ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" /> Saving…
                </>
              ) : (
                'Save changes'
              )}
            </Button>
          </div>
        }
      />

      {isLoading || !profile || !draft ? (
        <div className="space-y-4">
          <Skeleton className="h-40" />
          <Skeleton className="h-64" />
          <Skeleton className="h-64" />
        </div>
      ) : (
        <>
          {/* ----------------------------------------------------- Profile photo */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <User className="h-4 w-4 text-muted-foreground" />
                Profile photo
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex flex-col items-start gap-4 sm:flex-row sm:items-center">
                <Avatar className="h-20 w-20">
                  {profile.avatar_url && (
                    <AvatarImage src={profile.avatar_url} alt={profile.email} />
                  )}
                  <AvatarFallback className="text-base">{initials}</AvatarFallback>
                </Avatar>

                <div className="flex flex-col gap-2 sm:flex-row">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={onPickAvatar}
                    disabled={uploadAvatar.isPending || removeAvatar.isPending}
                  >
                    {uploadAvatar.isPending ? (
                      <>
                        <Loader2 className="h-4 w-4 animate-spin" /> Uploading…
                      </>
                    ) : (
                      <>
                        <Upload className="h-4 w-4" /> Upload new photo
                      </>
                    )}
                  </Button>
                  {profile.avatar_url && (
                    <Button
                      type="button"
                      variant="ghost"
                      onClick={onRemoveAvatar}
                      disabled={uploadAvatar.isPending || removeAvatar.isPending}
                      className="text-destructive hover:text-destructive"
                    >
                      {removeAvatar.isPending ? (
                        <>
                          <Loader2 className="h-4 w-4 animate-spin" /> Removing…
                        </>
                      ) : (
                        <>
                          <Trash2 className="h-4 w-4" /> Remove
                        </>
                      )}
                    </Button>
                  )}
                  {!profile.avatar_url && (
                    <span className="inline-flex items-center gap-2 text-xs text-muted-foreground">
                      <ImageOff className="h-4 w-4" />
                      No photo on file
                    </span>
                  )}
                </div>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/png,image/jpeg,image/webp,image/gif"
                  className="hidden"
                  onChange={onAvatarChange}
                />
              </div>
            </CardContent>
          </Card>

          {/* ----------------------------------------------------- Account info */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <AtSign className="h-4 w-4 text-muted-foreground" />
                Account
              </CardTitle>
            </CardHeader>
            <CardContent className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-1.5">
                <Label>Sign-in email</Label>
                <Input value={profile.email} disabled />
              </div>
              <div className="space-y-1.5">
                <Label>Role</Label>
                <div className="flex h-10 items-center">
                  <Badge variant="muted">{ROLE_LABELS[profile.role]}</Badge>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* ----------------------------------------------- Work Mode PIN */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <KeyRound className="h-4 w-4 text-muted-foreground" />
                Work Mode PIN
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex flex-col items-start gap-4 sm:flex-row sm:items-center sm:justify-between">
                <div className="flex flex-wrap items-center gap-2 text-sm">
                  {myCredential?.has_pin ? (
                    <Badge variant="success-soft">PIN set</Badge>
                  ) : (
                    <Badge variant="muted">No PIN yet</Badge>
                  )}
                  {myCredential?.has_badge ? (
                    <Badge variant="info-soft">Keyfob …{myCredential.badge_last4}</Badge>
                  ) : (
                    <span className="text-xs text-muted-foreground">No keyfob assigned</span>
                  )}
                  {myCredential?.locked_until && (
                    <Badge variant="warning">Locked — ask a manager</Badge>
                  )}
                </div>
                <Button type="button" variant="outline" onClick={() => setPinOpen(true)}>
                  <KeyRound className="h-4 w-4" />
                  {myCredential?.has_pin ? 'Change PIN' : 'Set PIN'}
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* ----------------------------------------------- Notifications */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Bell className="h-4 w-4 text-muted-foreground" />
                Notifications
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-5">
              <div className="flex items-center justify-between gap-4">
                <div className="space-y-1">
                  <Label htmlFor="notify_email" className="flex items-center gap-2">
                    <Mail className="h-3.5 w-3.5 text-muted-foreground" />
                    Email me when I&apos;m tagged
                  </Label>
                </div>
                <Switch
                  id="notify_email"
                  checked={profile.notify_email !== false}
                  disabled={update.isPending}
                  onCheckedChange={(checked) => {
                    void update
                      .mutateAsync({ notify_email: checked })
                      .then(() =>
                        toast.success(
                          checked ? 'Email notifications on' : 'Email notifications off',
                        ),
                      )
                      .catch((e) =>
                        toast.error(
                          e instanceof Error ? e.message : 'Could not update preference.',
                        ),
                      );
                  }}
                />
              </div>
              <div className="flex items-center justify-between gap-4">
                <div className="space-y-1">
                  <Label htmlFor="notify_slack" className="flex items-center gap-2">
                    <Slack className="h-3.5 w-3.5 text-muted-foreground" />
                    Slack DM me when I&apos;m tagged
                  </Label>
                </div>
                <Switch
                  id="notify_slack"
                  checked={profile.notify_slack !== false}
                  disabled={update.isPending}
                  onCheckedChange={(checked) => {
                    void update
                      .mutateAsync({ notify_slack: checked })
                      .then(() =>
                        toast.success(
                          checked ? 'Slack DMs on' : 'Slack DMs off',
                        ),
                      )
                      .catch((e) =>
                        toast.error(
                          e instanceof Error ? e.message : 'Could not update preference.',
                        ),
                      );
                  }}
                />
              </div>
              <Field
                label="Slack member ID (optional)"
                htmlFor="slack_member_id"
                icon={<Slack className="h-3.5 w-3.5" />}
              >
                <Input
                  id="slack_member_id"
                  value={draft.slack_member_id ?? ''}
                  onChange={(e) => setField('slack_member_id', e.target.value)}
                  placeholder="U012ABCDEF"
                  autoComplete="off"
                  aria-invalid={slackMemberIdInvalid || undefined}
                  aria-describedby="slack_member_id_help"
                />
                <p
                  id="slack_member_id_help"
                  className={
                    slackMemberIdInvalid
                      ? 'text-xs text-destructive'
                      : 'text-xs text-muted-foreground'
                  }
                >
                  {slackMemberIdInvalid
                    ? 'That isn’t a member ID. It starts with U or W, like U012ABCDEF — not your name or @handle.'
                    : `Atlas finds you in Slack by ${profile.email} and fills this in after your first DM. If your Slack account uses a different email, paste your ID: in Slack, click your profile picture → Profile → ⋮ (More) → Copy member ID.`}
                </p>
              </Field>
            </CardContent>
          </Card>

          {/* ----------------------------------------------- Dashboard privacy */}
          {showDashboardPrivacy && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <LayoutDashboard className="h-4 w-4 text-muted-foreground" />
                  Dashboard privacy
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-center justify-between gap-4">
                  <div className="space-y-1">
                    <Label htmlFor="dashboard-default-visible" className="flex items-center gap-2">
                      <Eye className="h-3.5 w-3.5 text-muted-foreground" />
                      Show dashboard by default
                    </Label>
                  </div>
                  <Switch
                    id="dashboard-default-visible"
                    checked={dashboardDefaultVisible}
                    onCheckedChange={(checked) => {
                      setDashboardDefaultVisible(checked);
                      writeDashboardDefaultVisible(checked);
                      toast.success(
                        checked
                          ? 'Dashboard will show on load'
                          : 'Dashboard will stay hidden on load',
                      );
                    }}
                  />
                </div>
              </CardContent>
            </Card>
          )}

          {/* ----------------------------------------------------- Personal */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <User className="h-4 w-4 text-muted-foreground" />
                Personal information
              </CardTitle>
            </CardHeader>
            <CardContent className="grid gap-4 sm:grid-cols-2">
              <Field label="Full name" htmlFor="full_name">
                <Input
                  id="full_name"
                  value={draft.full_name ?? ''}
                  onChange={(e) => setField('full_name', e.target.value)}
                  placeholder="Jane Doe"
                />
              </Field>
              <Field label="Pronouns" htmlFor="pronouns">
                <Input
                  id="pronouns"
                  value={draft.pronouns ?? ''}
                  onChange={(e) => setField('pronouns', e.target.value)}
                  placeholder="she/her"
                />
              </Field>
              <Field
                label="Phone"
                htmlFor="phone"
                icon={<Phone className="h-3.5 w-3.5" />}
              >
                <PhoneNumberInput
                  id="phone"
                  value={draft.phone ?? ''}
                  onChange={(v) => setField('phone', v)}
                />
              </Field>
              <Field label="Personal email" htmlFor="personal_email">
                <Input
                  id="personal_email"
                  type="email"
                  value={draft.personal_email ?? ''}
                  onChange={(e) => setField('personal_email', e.target.value)}
                  placeholder="jane@example.com"
                />
              </Field>
              <Field
                label="Date of birth"
                htmlFor="date_of_birth"
                icon={<Cake className="h-3.5 w-3.5" />}
              >
                <Input
                  id="date_of_birth"
                  type="date"
                  value={draft.date_of_birth ?? ''}
                  onChange={(e) => setField('date_of_birth', e.target.value)}
                />
              </Field>
              <Field label="Time zone" htmlFor="timezone">
                <select
                  id="timezone"
                  value={draft.timezone ?? ''}
                  onChange={(e) => setField('timezone', e.target.value)}
                  className="flex h-10 w-full rounded-lg border border-input bg-card px-3 py-2 text-sm text-foreground transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
                >
                  <option value="">— Pick a time zone —</option>
                  {timezones.map((tz) => (
                    <option key={tz} value={tz}>
                      {tz}
                    </option>
                  ))}
                </select>
              </Field>
              <div className="sm:col-span-2">
                <Field label="About" htmlFor="bio">
                  <Textarea
                    id="bio"
                    value={draft.bio ?? ''}
                    onChange={(e) => setField('bio', e.target.value)}
                    placeholder="A short bio teammates will see on your profile."
                    rows={3}
                  />
                </Field>
              </div>
            </CardContent>
          </Card>

          {/* ----------------------------------------------------- Employment */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Briefcase className="h-4 w-4 text-muted-foreground" />
                Employment
              </CardTitle>
            </CardHeader>
            <CardContent className="grid gap-4 sm:grid-cols-2">
              <Field label="Job title" htmlFor="job_title">
                <Input
                  id="job_title"
                  value={draft.job_title ?? ''}
                  onChange={(e) => setField('job_title', e.target.value)}
                  placeholder="Operations Manager"
                />
              </Field>
              <Field label="Start date" htmlFor="start_date">
                <Input
                  id="start_date"
                  type="date"
                  value={draft.start_date ?? ''}
                  onChange={(e) => setField('start_date', e.target.value)}
                />
              </Field>
            </CardContent>
          </Card>

          {/* ----------------------------------------------------- Address */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Home className="h-4 w-4 text-muted-foreground" />
                Mailing address
              </CardTitle>
            </CardHeader>
            <CardContent className="grid gap-4 sm:grid-cols-2">
              <div className="sm:col-span-2">
                <Field label="Street address" htmlFor="address_line1">
                  <Input
                    id="address_line1"
                    value={draft.address_line1 ?? ''}
                    onChange={(e) => setField('address_line1', e.target.value)}
                    placeholder="123 Main St."
                  />
                </Field>
              </div>
              <div className="sm:col-span-2">
                <Field label="Apt / Suite" htmlFor="address_line2">
                  <Input
                    id="address_line2"
                    value={draft.address_line2 ?? ''}
                    onChange={(e) => setField('address_line2', e.target.value)}
                    placeholder="Apt 4B"
                  />
                </Field>
              </div>
              <Field
                label="City"
                htmlFor="city"
                icon={<MapPin className="h-3.5 w-3.5" />}
              >
                <Input
                  id="city"
                  value={draft.city ?? ''}
                  onChange={(e) => setField('city', e.target.value)}
                  placeholder="Asheville"
                />
              </Field>
              <Field label="State / Region" htmlFor="state">
                <Input
                  id="state"
                  value={draft.state ?? ''}
                  onChange={(e) => setField('state', e.target.value)}
                  placeholder="NC"
                />
              </Field>
              <Field label="Postal code" htmlFor="postal_code">
                <Input
                  id="postal_code"
                  value={draft.postal_code ?? ''}
                  onChange={(e) => setField('postal_code', e.target.value)}
                  placeholder="28801"
                />
              </Field>
              <Field label="Country" htmlFor="country">
                <Input
                  id="country"
                  value={draft.country ?? ''}
                  onChange={(e) => setField('country', e.target.value)}
                  placeholder="United States"
                />
              </Field>
            </CardContent>
          </Card>

          {/* ----------------------------------------------------- Emergency contact */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <ShieldAlert className="h-4 w-4 text-muted-foreground" />
                Emergency contact
              </CardTitle>
            </CardHeader>
            <CardContent className="grid gap-4 sm:grid-cols-2">
              <Field label="Contact name" htmlFor="emergency_contact_name">
                <Input
                  id="emergency_contact_name"
                  value={draft.emergency_contact_name ?? ''}
                  onChange={(e) => setField('emergency_contact_name', e.target.value)}
                  placeholder="John Doe"
                />
              </Field>
              <Field label="Relationship" htmlFor="emergency_contact_relationship">
                <Input
                  id="emergency_contact_relationship"
                  value={draft.emergency_contact_relationship ?? ''}
                  onChange={(e) => setField('emergency_contact_relationship', e.target.value)}
                  placeholder="Spouse"
                />
              </Field>
              <Field label="Contact phone" htmlFor="emergency_contact_phone">
                <PhoneNumberInput
                  id="emergency_contact_phone"
                  value={draft.emergency_contact_phone ?? ''}
                  onChange={(v) => setField('emergency_contact_phone', v)}
                />
              </Field>
            </CardContent>
          </Card>

          {/* Sticky save bar — duplicates the header actions for long
              forms so the user doesn't have to scroll all the way back
              up after editing the bottom card. Only visible when there
              are unsaved changes. */}
          {isDirty && (
            <div className="sticky bottom-4 z-10 flex justify-end">
              <div className="flex items-center gap-2 rounded-xl border border-border bg-card px-4 py-2 shadow-md">
                <span className="text-sm text-muted-foreground">
                  You have unsaved changes
                </span>
                <Button variant="ghost" size="sm" onClick={onReset} disabled={update.isPending}>
                  Discard
                </Button>
                <Button size="sm" onClick={onSave} disabled={update.isPending}>
                  {update.isPending ? (
                    <>
                      <Loader2 className="h-3.5 w-3.5 animate-spin" /> Saving…
                    </>
                  ) : (
                    'Save changes'
                  )}
                </Button>
              </div>
            </div>
          )}

          {authProfile?.id !== profile.id && (
            // Belt-and-suspenders: should never happen, but if the
            // AuthProvider snapshot somehow drifts from the loaded row
            // we'd rather surface that than silently mutate the wrong
            // profile.
            <p className="text-xs text-destructive">
              Profile mismatch detected — please sign out and back in.
            </p>
          )}
        </>
      )}

      <SetPinDialog self person={pinPerson} onOpenChange={(open) => !open && setPinOpen(false)} />
    </div>
  );
}

interface FieldProps {
  label: string;
  htmlFor: string;
  icon?: React.ReactNode;
  children: React.ReactNode;
}

function Field({ label, htmlFor, icon, children }: FieldProps) {
  return (
    <div className="space-y-1.5">
      <Label htmlFor={htmlFor} className="flex items-center gap-1.5">
        {icon && <span className="text-muted-foreground">{icon}</span>}
        {label}
      </Label>
      {children}
    </div>
  );
}
